import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CancelEvent,
  DateChangeEvent,
  DragEndEvent,
  EventClickEvent,
  EventStyleArgs,
  RemoveEvent,
  SaveEvent,
  SchedulerComponent,
  SchedulerEvent,
  SlotClassArgs,
  SlotClickEvent,
} from '@progress/kendo-angular-scheduler';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Day } from '@progress/kendo-date-math';
import { cancelIcon, clockIcon, exportIcon, lockIcon, plusIcon, SVGIcon, tablePositionCenterIcon, unlockIcon, xIcon } from '@progress/kendo-svg-icons';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  ICreateSchedulerItemRequest,
  IKendoSchedulerResource,
  ISchedulerEvent,
  ISchedulerItem,
  ISearchSchedulerItemRequest,
  IUpdateSchedulerItemRequest,
} from 'src/app/scheduler/models/scheduler';
import { ResourcePlanerService } from 'src/app/scheduler/services/resource-planer.service';
import { IHoliday } from 'src/app/setting/holiday/model/holiday';
import { IRessource } from 'src/app/setting/models/ressource';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import {
  AbsenceState,
  AbsenceType,
  IAbsencePlanning,
} from 'src/app/time-tracking/models/absence-group';
import { ITimeEntryCategory } from 'src/app/time-tracking/models/time-entry-category';
import { TimeEntryCategoryService } from 'src/app/time-tracking/services/time-entry-category.service';
import { getCalendarWeekFromDate } from '../helpers/date-helpers';
import { TimeSpan } from '../helpers/time-span';
import { AppNotificationService } from '../services/notification.service';
import {
  DragTargetDragStartEvent,
  DragTargetDragEndEvent,
  DropTargetEvent,
  DragTargetContainerDirective,
  DropTargetContainerDirective,
} from "@progress/kendo-angular-utils";
import { IAssignment, IAssignmentInfo, ISearchAssignmentsRequest, TaskStatues } from 'src/app/assignment/models/assignment';
import { AssignmentService } from 'src/app/assignment/services/assignment.service';
import { ITenantSettings } from 'src/app/setting/tenant-settings/models/tenant-setting';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { WorkTimeSchedule, WorkTimeScheduleService } from '../services/work-time-schedule.service';


@UntilDestroy()
@Component({
    selector: 'app-scheduler-shared',
    templateUrl: './scheduler-shared.component.html',
    styleUrls: ['./scheduler-shared.component.css'],
    standalone: false
})
export class SchedulerSharedComponent implements OnInit {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private timeEntryCategoryService: TimeEntryCategoryService,
    private resourcePlanerService: ResourcePlanerService,
    private appnotificationService: AppNotificationService,
    private tenantSettingsStore: TenantSettingsStoreService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private assigmentService: AssignmentService,
    private permissionStore: PermissionStorageService,
    private workTimeSchedules: WorkTimeScheduleService
  ) {
    this.createFormGroup = this.createFormGroup.bind(this);
  }

  events$ = new BehaviorSubject<ISchedulerItem[]>([]);
  absenceGroups$ = new BehaviorSubject<IAbsencePlanning[]>([]);

  @Input() showAssigmentList: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() holidays: Array<IHoliday>;
  @Input() schedulerRessources: Array<IRessource> = [];
  @Input() searchRequest: ISearchSchedulerItemRequest;
  @Input() assignmentId: string;
  @Input() set events(value: Array<ISchedulerItem>) {
    this.events$.next(value);
  }
  // IAbsenceGroup (full detail) is structurally compatible, so callers with team data still fit.
  @Input() set absenceGroups(value: Array<IAbsencePlanning>) {
    this.absenceGroups$.next(value);
  }
  @Output() onReloadEvents: EventEmitter<undefined> = new EventEmitter();

  destroy$ = new Subject();

  timeEntryCategories$ = new BehaviorSubject<ITimeEntryCategory[]>(null);

  isLocked = false;
  isEditable: any = true;
  canEditSchedulerItems = false;
  isReadonlyDialog = false;

  get Day(){
    return Day;
  }

  slotDurinationConfig = [
    {
      name: 'timeline',
      slotDuration: 30
    },
    {
      name: 'timelineWeek',
      slotDuration: 300
    },
    {
      name: 'timelineMonth',
      slotDuration: 540
    }
  ]

  public weekStart: Day = Day.Monday;

  public resources: IKendoSchedulerResource[] = [];

  /** Precomputed worktime schedules keyed by ressource id (only employee ressources). */
  private resourceWorkTimes = new Map<string, WorkTimeSchedule[]>();

  scrollTime: Date = new Date();

  public selectedDate: Date = new Date();
  selectedViewIndex$ = new BehaviorSubject<number>(0);
  

  public formGroup!: UntypedFormGroup;

  weekNumber:number = getCalendarWeekFromDate(new Date());
  weekNumberEnd:number;

  public group: any = {
    resources: ['Ressources'],
    orientation: 'vertical',
  };

  get absenceTypes() {
    return AbsenceType;
  }

  get absenceStates() {
    return AbsenceState;
  }

  isMobileView = false;

  public isDropValid = false;
  
  public plusIcon: SVGIcon = plusIcon;
  public lockIcon: SVGIcon = lockIcon;
  public unlockIcon: SVGIcon = unlockIcon;
  public exportIcon: SVGIcon = exportIcon;
  public clockIcon: SVGIcon = clockIcon;
  public xIcon: SVGIcon = xIcon;
  public cancleIcon: SVGIcon = cancelIcon;
  public toolbarWidth: number;

  public currentData : IAssignmentInfo = {
    id: null,
    customerName: "",
    title: "",
  };

  // assigments$ = new Subject<IAssignment[]>();
  assignmentRefresh$ = new BehaviorSubject<ISearchAssignmentsRequest>({
    pageNumber: 1, 
    pageSize: 100, 
    taskStatuses: [TaskStatues.open, TaskStatues.inProgress],
    orderBy: ['dueDate'],
    hasNoSchedulerItems: true
  })

  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @ViewChild("dragWrapper", { read: DragTargetContainerDirective })
  public dragTargetContainer: DragTargetContainerDirective;

  @ViewChild("dropWrapper", { read: DropTargetContainerDirective })
  public dropTargetContainer: DropTargetContainerDirective;

  vm$ = combineLatest([
    this.assignmentRefresh$.pipe(
      switchMap(request =>
        this.hasPermission('Permissions.Assignments.Search')
          ? this.assigmentService.searchAssignments(request)
          : of({ data: [], totalCount: 0 })
      )
    ),
    this.tenantSettingsStore.tenantSettings$,
    this.selectedViewIndex$.pipe(
      withLatestFrom(this.tenantSettingsStore.tenantSettings$),
      map(([index,tenantSettings])=> ({index,tenantSettings})),
      tap(data => this.setSlotDurationConfig(data.index,data.tenantSettings)),
      map(data => data.index)
    )
  ]).pipe(
    map(([
      assigments,
      tenantSettings,
      selectedViewIndex
    ]) => ({
      assigments,
      tenantSettings,
      selectedViewIndex
    }))
  );

  ngOnInit(): void {
    this.initResources(this.schedulerRessources);
    this.loadWorkTimes(this.schedulerRessources);

    this.listSchedulerItemTypes();
    this.setMobileView();

    this.setSchedulerWithUrlParams();

    // this.loadAssignments();

    this.canEditSchedulerItems = this.hasPermission('Permissions.SchedulerItems.Update');
    this.updateEditable();
  }

  toggleLock() {
    this.isLocked = !this.isLocked;
    this.updateEditable();
  }

  private updateEditable() {
    if (this.isMobileOrTablet() || this.isLocked) {
      this.isEditable = false;
    } else {
      this.isEditable = {
        remove: this.hasPermission('Permissions.SchedulerItems.Delete'),
        drag: this.hasPermission('Permissions.SchedulerItems.Update'),
        resize: this.hasPermission('Permissions.SchedulerItems.Update')
      };
    }
  }

  @HostListener('window:resize', ['$event'])
  setMobileView(event?) {
    var scrWidth = window.innerWidth;

    if (scrWidth < 600) {
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }

  isMobileOrTablet(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Basic mobile/tablet detection
    const isMobile = /android|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent);

    // Optional: Add screen size check
    const isSmallScreen = window.matchMedia('(max-width: 1024px)').matches;

    return this.isTouchDevice() && (isMobile || isSmallScreen);
  }

  isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  listSchedulerItemTypes() {
    if (!this.hasPermission('Permissions.TimeEntryCategories.Search')) {
      return;
    }
    this.timeEntryCategoryService
      .searchTimeEntryCategories({
        pageNumber: 0,
        pageSize: 999999,
      })
      .subscribe((response) => {
        this.timeEntryCategories$.next(response.data);
      });
  }

  initResources(ressources: IRessource[]) {
    const ressourcesResources: IKendoSchedulerResource = {
      name: 'Ressources',
      data: [],
      field: 'ressourceIds',
      multiple: true,
      valueField: 'id',
      textField: 'text',
    };

    ressources.sort(function (ressource1, ressource2) {
      if (
        ressource1.ressourceCategory == null &&
        ressource2.ressourceCategory != null
      )
        return -1;
      if (
        ressource1.ressourceCategory != null &&
        ressource2.ressourceCategory == null
      )
        return 1;

      if (
        ressource1.ressourceCategory?.name > ressource2.ressourceCategory?.name
      )
        return 1;
      if (
        ressource1.ressourceCategory?.name < ressource2.ressourceCategory?.name
      )
        return -1;

      if (
        ressource1.employee?.department.name >
        ressource2.employee?.department.name
      )
        return 1;
      if (
        ressource1.employee?.department.name <
        ressource2.employee?.department.name
      )
        return -1;

      if (ressource1.name > ressource2.name) return 1;
      if (ressource1.name < ressource2.name) return -1;
    });

    ressources.forEach((ressource) => {
      const ressourceResource = {
        text: `${ressource.name}`,
        shortText: (ressource.nameShort != null && ressource.nameShort !== "") ? ressource.nameShort : ressource.name.substring(0,4),
        id: ressource.id,
        color: ressource.ressourceCategory?.color ?? ressource.employee?.department?.schedulerColor,
        department: ressource.employee?.department?.name,
      };
      ressourcesResources.data.push(ressourceResource);
    });
    this.resources.push(ressourcesResources);
  }

  public createFormGroup(args: any): UntypedFormGroup {
    const dataItem = args;

    this.formGroup = this.formBuilder.group(
      {
        id: dataItem?.id ?? null,
        start: [dataItem.start, Validators.required],
        end: [dataItem.end, Validators.required],
        startTimezone: [dataItem.startTimezone],
        endTimezone: [dataItem.endTimezone],
        isAllDay: dataItem.isAllDay,
        title: [dataItem.title, Validators.required],
        description: dataItem.description,
        recurrenceRule: dataItem.recurrenceRule,
        recurrenceId: dataItem.recurrenceId,
        timeEntryCategory: [dataItem.timeEntryCategory, Validators.required],
        ressourceIds: [dataItem.ressourceIds, Validators.required],
        assignmentId: dataItem.assignmentId,
      },
      {}
    );

    return this.formGroup;
  }

  // public getNextId(): number {
  //   const len = this.events.length;

  //   return len === 0 ? 1 : this.events[this.events.length - 1].id + 1;
  // }

  onDateChange(event: DateChangeEvent): void {
    this.searchRequest.start = event.dateRange.start;
    this.searchRequest.end = event.dateRange.end;
    this.onReloadEvents.emit();

    this.weekNumber = getCalendarWeekFromDate(event.dateRange.start);
    this.weekNumberEnd = getCalendarWeekFromDate(event.dateRange.end);

    this.updateUrlParameters(event.dateRange.start, event.dateRange.end,event.sender.selectedView.name);
  }

  validateStartEnd(): void {
    const start = this.formGroup.get('start').value;
    const end = this.formGroup.get('end').value;

    if (start && end && start >= end) {
      const newEnd = new Date(start.getTime() + 30 * 60000);
      this.formGroup.get('end').patchValue(newEnd);
    }
  }

  validateEndStart(): void {
    const start = this.formGroup.get('start').value;
    const end = this.formGroup.get('end').value;

    if (start && end && start >= end) {
      const newEnd = new Date(new Date(start).getTime() + 30 * 60000);
      this.formGroup.get('end').patchValue(newEnd);
    }
  }

  private setNoonTime(date: Date): Date {
    if (!date) return date;
    const result = new Date(date);
    result.setHours(12, 0, 0, 0);
    return result;
  }

  setBorderColor = (args: EventStyleArgs) => {
    let ngStyleObj: Object;
    if (this.assignmentId && args.event.dataItem?.absenceType == null) {
      ngStyleObj = {
        'background-color': `${args.event.dataItem.timeEntryCategory.color}`,
        opacity:
          args.event.dataItem.assignmentId == this.assignmentId ? '1' : '0.4',
        border:
          args.event.dataItem.assignmentId == this.assignmentId
            ? 'black solid 2px'
            : 'none',
      };
    } else {
      if (args.event.dataItem?.absenceType == this.absenceTypes.Compensation) {
        ngStyleObj = {
          'background-color': '#fd9f3e',
        };
      } else if (args.event.dataItem?.absenceType != null) {
        ngStyleObj = {
          'background-color': 'red',
        };
      } else {
        ngStyleObj = {
          'background-color': `${args.event.dataItem?.timeEntryCategory?.color}`,
        };
      }
    }

    return ngStyleObj;
  };

  setSlotBackgroundColor = (args: SlotClassArgs) => {
    const classes: string[] = [];

    const activeHoliday = (this.holidays ?? []).filter(
      (holiday) =>
        new Date(holiday.start).toDateString() == args.start.toDateString()
    );
    if (activeHoliday.length) {
      classes.push('holiday');
    }

    if (this.isNonWorkingSlot(args)) {
      classes.push('non-working-time');
    }

    return classes.length ? classes : undefined;
  };

  /**
   * Loads the worktime of every employee ressource currently shown in the planner
   * and precomputes the per-weekday working intervals used to gray out non-working slots.
   * Worktime is fetched per employee (one request each) and failures are swallowed so a
   * missing/forbidden worktime never breaks the planner.
   */
  private loadWorkTimes(ressources: IRessource[]): void {
    const employeeToRessourceIds = new Map<string, string[]>();
    (ressources ?? []).forEach((ressource) => {
      const employeeId = ressource.employee?.id;
      if (!employeeId) {
        return;
      }
      const ressourceIds = employeeToRessourceIds.get(employeeId) ?? [];
      ressourceIds.push(ressource.id);
      employeeToRessourceIds.set(employeeId, ressourceIds);
    });

    const employeeIds = Array.from(employeeToRessourceIds.keys());
    if (!employeeIds.length) {
      return;
    }

    this.workTimeSchedules
      .loadSchedules(employeeIds)
      .pipe(untilDestroyed(this))
      .subscribe((schedulesByEmployee) => {
        schedulesByEmployee.forEach((schedules, employeeId) => {
          (employeeToRessourceIds.get(employeeId) ?? []).forEach((ressourceId) =>
            this.resourceWorkTimes.set(ressourceId, schedules)
          );
        });

        // Reassign the resources input so the scheduler re-renders its slots and
        // the newly loaded worktimes are applied via the slotClass callback.
        this.resources = [...this.resources];
      });
  }

  /**
   * Returns true when the given slot lies outside the working hours of the employee
   * assigned to the slot's ressource. Slots without worktime data (e.g. non-employee
   * ressources) are never treated as non-working, so they stay untouched.
   */
  private isNonWorkingSlot(args: SlotClassArgs): boolean {
    const ressourceId = args.resources?.[0]?.id;
    if (!ressourceId) {
      return false;
    }
    return this.workTimeSchedules.isNonWorkingSlot(
      this.resourceWorkTimes.get(ressourceId),
      args
    );
  }

  onDragEnd(value: DragEndEvent) {
    if (!this.hasPermission('Permissions.SchedulerItems.Update')) {
      return;
    }
    console.log('drag:', value);
  }

  public slotDblClickHandler(event: SlotClickEvent): void {
    if (!this.hasPermission('Permissions.SchedulerItems.Create')) {
      return;
    }
    this.closeEditor(event.sender);

    this.formGroup = this.formBuilder.group({
      start: [event.start, Validators.required],
      end: [event.end, Validators.required],
      startTimezone: new UntypedFormControl(),
      endTimezone: new UntypedFormControl(),
      isAllDay: event.isAllDay,
      title: new UntypedFormControl('', Validators.required),
      description: new UntypedFormControl(''),
      recurrenceRule: new UntypedFormControl(),
      recurrenceID: new UntypedFormControl(),

      assignmentId: new UntypedFormControl(this.assignmentId ?? null),
      timeEntryCategory: new UntypedFormControl(null, Validators.required),
      ressourceIds: new UntypedFormControl(
        [event.resources[0].id],
        Validators.required
      ),
    });

    event.sender.addEvent(this.formGroup);
    this.initTitleValueBasedOnTimeEntryCategory();
  }

  public eventDblClickHandler({ sender, event }: EventClickEvent): void {
    if (event.dataItem.absenceType == null) {
      this.closeEditor(sender);

      let dataItem = event.dataItem;
      this.formGroup = this.createFormGroup(dataItem);

      if (!this.canEditSchedulerItems) {
        this.isReadonlyDialog = true;
        this.formGroup.disable();
      } else {
        this.isReadonlyDialog = false;
      }

      sender.editEvent(dataItem, { group: this.formGroup });
      this.initTitleValueBasedOnTimeEntryCategory();
    }
  }

  public dragEndHandler(event: DragEndEvent): void {
    if (!this.hasPermission('Permissions.SchedulerItems.Update')) {
      return;
    }
    let value = {
      start: event.start,
      end: event.end,
      isAllDay: event.isAllDay,
      ressources: event.resources.ressourceIds,
    };
    let dataItem = event.dataItem;
    this.handleUpdate(dataItem, value);
  }

  public resizeEndHandler({ sender, event, start, end }): void {
    if (!this.hasPermission('Permissions.SchedulerItems.Update')) {
      return;
    }
    let value = { start: start, end: end };
    let dataItem = event.dataItem;
    this.handleUpdate(dataItem, value);
  }

  saveHandler(event: SaveEvent) {
    if (event.isNew) {
      this.resourcePlanerService
        .addEvent(this.createRequest(event.formGroup.value))
        .pipe(untilDestroyed(this))
        .subscribe((response) => {
          this.onReloadEvents.emit();
          this.appnotificationService.notification$.next({
            message: translate('scheduler-shared.createSuccess'),
            style: 'success',
          });

          this.assignmentRefresh$.next(this.assignmentRefresh$.value)
        });
    } else {
      this.handleUpdate(event.formGroup.value, {
        start: event.formGroup.value.start,
        end: event.formGroup.value.end,
      });
    }
    this.closeEditor(event.sender);
  }

  cancelHandler(event: CancelEvent) {
    this.closeEditor(event.sender);
  }

  private closeEditor(scheduler: SchedulerComponent): void {
    scheduler.closeEvent();

    this.isReadonlyDialog = false;
    this.formGroup = undefined;
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  public removeHandler({ sender, dataItem }: RemoveEvent): void {
    if (!this.hasPermission('Permissions.SchedulerItems.Delete')) {
      return;
    }
    if (dataItem.absenceType == null) {
      sender.openRemoveConfirmationDialog().subscribe((shouldRemove) => {
        if (shouldRemove) {
          this.resourcePlanerService
            .deleteEvent(dataItem.id)
            .pipe(untilDestroyed(this))
            .subscribe((response) => {
              this.onReloadEvents.emit();
              this.closeEditor(sender);
              this.appnotificationService.notification$.next({
                message: translate('scheduler-shared.deleteSuccess'),
                style: 'success',
              });
              this.assignmentRefresh$.next(this.assignmentRefresh$.value)
            });
        }
      });
    }
  }

  createRequest(event: ISchedulerEvent): ICreateSchedulerItemRequest {
    const start = event.isAllDay ? this.setNoonTime(event.start) : event.start;
    const end = event.isAllDay ? this.setNoonTime(event.end) : event.end;

    const request: ICreateSchedulerItemRequest = {
      start: start,
      end: end ?? null,
      title: event.title ?? '',
      description: event.description ?? '',
      isAllDay: event.isAllDay ?? false,
      recurrenceRule: event.recurrenceRule,
      timeEntryCategoryId: event.timeEntryCategory.id,
      ressourceIds: event.ressourceIds,
      assignmentId: this.assignmentId ? this.assignmentId : event.assignmentId,
    };

    console.log(request, 'edit service create request');
    return request;
  }

  updateRequest(
    event: ISchedulerEvent,
    value: { start: Date; end: Date; ressources: string[] }
  ): IUpdateSchedulerItemRequest {
    const isAllDay = event.isAllDay ?? false;
    const start = isAllDay ? this.setNoonTime(value.start ?? event.start) : (value.start ?? event.start);
    const end = isAllDay ? this.setNoonTime(value.end ?? event.end) : (value.end ?? event.end);

    const request: IUpdateSchedulerItemRequest = {
      start: start,
      end: end,
      title: event.title ?? '',
      description: event.description ?? '',
      isAllDay: isAllDay,
      id: event.id ?? null,
      recurrenceRule: event.recurrenceRule,
      timeEntryCategoryId: event.timeEntryCategory.id,
      ressourceIds: value.ressources ?? event.ressourceIds,
      assignmentId: event.assignmentId,
    };
    return request;
  }

  private handleUpdate(item: any, value: any): void {
    // The item is non-recurring or we are editing the entire series.
    if(item.absenceType == null){
    this.resourcePlanerService
      .updateEvent(this.updateRequest(item, value))
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.onReloadEvents.emit();
        this.appnotificationService.notification$.next({
          message: translate('scheduler-shared.saveSuccess'),
          style: 'success',
        });
        this.assignmentRefresh$.next(this.assignmentRefresh$.value)
      });
    }
  }

  initTitleValueBasedOnTimeEntryCategory() {
    this.formGroup
      .get('timeEntryCategory')
      .valueChanges.pipe(takeUntil(this.destroy$), untilDestroyed(this))
      .subscribe((value) => {
        this.formGroup.get('title').patchValue(value.name);
      });
  }

  // create event Objects based on AbsenceGroups and SchedulerItem

  schedulerEvents$: Observable<ISchedulerItem[]> = combineLatest([
    this.events$,
    this.absenceGroups$,
    this.tenantSettingsStore.tenantSettings$
  ]).pipe(
    map(([events, absenceGroups, tenantSettings]) => ({
      events,
      absenceGroups,
      tenantSettings
    })),
    switchMap(({ events, absenceGroups, tenantSettings }) => {
      let schedulerEvents: ISchedulerItem[] = [];

      events.map((event) => {
        const newEvent: ISchedulerItem = {
          title: event.title,
          start: new Date(event.start),
          end: new Date(event.end),
          ressourceIds: event.ressourceIds,
          ressources: event.ressources,
          description: event.description,
          isAllDay: event.isAllDay,
          recurrenceRule: event.recurrenceRule,
          timeEntryCategoryId: event.timeEntryCategoryId,
          timeEntryCategory: event.timeEntryCategory,
          employeeIds: event.employeeIds,
          employees: event.employees,
          assignmentId: event.assignmentId,
          assignment: event.assignment,
          id: event.id
        };

        schedulerEvents.push(newEvent);
      });

      absenceGroups.map((absenceGroup) => {
        if (absenceGroup.absenceState != this.absenceStates.Declined) {

          let startTime = new Date(absenceGroup.dateFrom);
          let endTime = new Date(
            new Date(absenceGroup.dateTo).setUTCHours(23, 59, 59, 999)
          );

          // absence in the morning
          if(absenceGroup.isHalfDayEnd){
            const dayEndTimeSpanObj = TimeSpan.parse(tenantSettings.endMorningWorkTime).getAsTimeObject();
            endTime = new Date(
              new Date(absenceGroup.dateTo).setHours(dayEndTimeSpanObj.hours, dayEndTimeSpanObj.minutes)
            );
          } 
          if ( absenceGroup.isHalfDayStart){
            // absence in the afternoon
            const dayStartTimeSpanObj = TimeSpan.parse(tenantSettings.startAfternoonWorkTime).getAsTimeObject();
            startTime = new Date(
              new Date(absenceGroup.dateFrom).setHours(dayStartTimeSpanObj.hours, dayStartTimeSpanObj.minutes)
            );

          }

          // Outside the viewer's supervisor reach the backend redacts type and title: show "absent".
          const typeLabel = absenceGroup.isRedacted
            ? translate('absenceGroups.absent')
            : translate('absenceGroups.absenceTypes.' + this.absenceTypes[absenceGroup.absenceType]);
          const stateLabel = translate(
            'absenceGroups.absenceStates.' + this.absenceStates[absenceGroup.absenceState]
          );

          const newEvent: ISchedulerItem = {
            title: `${typeLabel} (${stateLabel})
             ${absenceGroup?.title ?? ''}`,
            start: startTime,
            end: endTime,
            description: `${stateLabel}
             ${absenceGroup?.title ?? ''}`,
            employeeIds: [absenceGroup.employee.id],
            employees: [absenceGroup.employee],
            ressourceIds: [absenceGroup.employee.ressource.id],
            ressources: [absenceGroup.employee.ressource],
            absenceType: absenceGroup.absenceType,
            id: absenceGroup.id
          };

          schedulerEvents.push(newEvent);
        }
      });

      return [schedulerEvents];
    })
  );
  
  updateUrlParameters(startDate: Date, endDate: Date, view: string) {
    // if toString() methode is used then the Timezone will set the day one day in the past
    let year = startDate.getFullYear();
    let month = startDate.getMonth() + 1; // getMonth() returns 0-11
    let day = startDate.getDate();
    let startDateString = `${year}-${month}-${day}`;

    const queryParams = { startdate: startDateString, view };

    this.router.navigate(
      [], 
      {
        relativeTo: this.activatedRoute, // Ensure that you inject ActivatedRoute
        queryParams: queryParams,
        queryParamsHandling: 'merge', // preserve the existing query params in the route
        replaceUrl: true, // does not add a new entry in the browser's history
        skipLocationChange: false // retains the current URL while updating the parameters
      });
  }

  setSchedulerWithUrlParams(){
    var urlDate = this.activatedRoute.snapshot.queryParams?.startdate;
    if(urlDate){
      this.selectedDate = new Date(urlDate);
    }
   

    var selectedView = this.activatedRoute.snapshot.queryParams?.view;

    if(selectedView == 'timelineWeek'){
      this.selectedViewIndex$.next(1);
    }
    else if(selectedView == 'timelineMonth'){
      this.selectedViewIndex$.next(2);
    }
    else {
      this.selectedViewIndex$.next(0);
    }
  }


  // Drag & Drop Function
  public onDrop(ev: DropTargetEvent, scheduler: SchedulerComponent): void {
    const dropTarget = ev.dropTarget;

    if (this.isDropValid) {
      const startDate = new Date(
        ev.dropTarget.querySelector(".slotDate").getAttribute("slotDate")
      );

      const viewConfigArray = scheduler.views.toArray();
      const viewConfig = viewConfigArray[scheduler.selectedViewIndex];
      const tmp = TimeSpan.parse(scheduler.workDayEnd);
      const tmp2 = tmp.getTotalSeconds();
      const tmp3 = viewConfigArray[scheduler.selectedViewIndex].name;
      const tmp4 = viewConfigArray[scheduler.selectedViewIndex].title;

      const slotConfig = this.slotDurinationConfig.find(x => x.name == viewConfig.name);

      const slotDurationCalc = (TimeSpan.parse(scheduler.workDayEnd).getTotalSeconds() - TimeSpan.parse(scheduler.workDayStart).getTotalSeconds()) / scheduler.slotDivisions;

      // TODO: Replace with action slot Duration
      const endDate = new Date(startDate.getTime() + slotConfig.slotDuration * 60000);
      // const endDate = new Date(startDate.getTime() + 60 * 60000);
      const resourceId = ev.dropTarget
        .querySelector(".slotDate")
        .getAttribute("resourceId");
      const isTaskAllDay: boolean =
        ev.dropTarget.querySelector(".slotDate").getAttribute("allDay") ===
        "true";


      const assignmentId = ev.dragTarget
      .querySelector(".k-card")
      .getAttribute("assigmentId");

      const title = ev.dragTarget
      .querySelector(".k-card")
      .getAttribute("title");
     


      let event : ISchedulerEvent = {
        title: title,
        id: null,
        start:startDate,
        end: endDate,
        ressourceIds:[resourceId],
        assignmentId: assignmentId
      }

      const group = this.createFormGroup(event);
      

      scheduler.addEvent(group);
      this.removeHighlight(dropTarget);
    }
  }

  public onDragEnter(ev: DropTargetEvent) {
    console.log("drag enter")
    const dropTarget = ev.dropTarget;

    if (this.isDropValid) {
      this.addHighlight(dropTarget);
    }
  }

  public onDragLeave(ev: DropTargetEvent) {
    const dropTarget = ev.dropTarget;

    this.removeHighlight(dropTarget);
    console.log("drag leave")
  }

  public onDragStart(ev: DragTargetDragStartEvent) {
    this.currentData = this.getDragTargetData(ev.dragTarget);
  }
  public onDrag(ev: DragTargetDragEndEvent) {
    const eventTarget = ev.dragEvent.originalEvent.target["classList"].value;

    
    if (
      eventTarget === "drop-zone" ||
      eventTarget === "drop-zone ng-star-inserted" ||
      eventTarget === "drop-zone ng-star-inserted timeSlotHighlight" ||
      eventTarget === "drop-zone timeSlotHighlight" 
    ) {
      this.isDropValid = true;
    } else {
      this.isDropValid = false;
    }
    console.log({event: eventTarget, isDropValid: this.isDropValid}, "eventTarget")
  }

  private addHighlight(target: HTMLElement) {
    target && this.renderer.addClass(target, "timeSlotHighlight");
  }
  private removeHighlight(target: HTMLElement) {
    target && this.renderer.removeClass(target, "timeSlotHighlight");
  }

  private getDragTargetData(dragTarget: HTMLElement){

    const assignmentId = dragTarget
    .querySelector(".k-card")
    .getAttribute("assigmentId");

    const title = dragTarget
    .querySelector(".k-card")
    .getAttribute("title");

    const customerName = dragTarget
    .querySelector(".k-card")
    .getAttribute("customerName");

    const extractedInfo: IAssignmentInfo = {
      id: assignmentId,
      customerName: customerName,
      title: title,
    };

    return extractedInfo;
  }

  onViewChange(index, tenantSettings : ITenantSettings){

    this.setSlotDurationConfig(index,tenantSettings);

    this.selectedViewIndex$.next(index);
    this.dragTargetContainer.notify();
    this.dropTargetContainer.notify();
  }

  setSlotDurationConfig(index, tenantSettings : ITenantSettings){
    
    if(tenantSettings != null)
    {
      const  dayDurationSeconds = TimeSpan.parse(tenantSettings.endAfternoonWorkTime).getTotalSeconds() - TimeSpan.parse(tenantSettings.startMorningWorkTime).getTotalSeconds();
      
      const dayDurationMinutes = dayDurationSeconds / 60;

      let slotDuration = 30;

      switch(index){
        case 1:{
          slotDuration = dayDurationMinutes / 2;
          break;
        }
        case 2: {
          slotDuration = dayDurationMinutes;
          break;
        }
        default:{
          slotDuration = this.slotDurinationConfig[0].slotDuration;
          break;
        }
      }

      this.slotDurinationConfig[index].slotDuration = slotDuration;
    }
  }

  getSlotDuration(searchString: string){
    return this.slotDurinationConfig.find(x => x.name == searchString)?.slotDuration
  }

  public onSchedulerResize(width: number): void {
    this.toolbarWidth = width;
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }
}
